import LoadingScreen from 'components/LoadingScreen';
import { useDetectDevice, useLocationConsent } from 'hooks';
import { PudoLocation } from '@pperform/pp-data-mappers';
import { FunctionComponent } from 'preact';
import { useEffect } from 'preact/hooks';
import { useTranslation } from 'react-i18next';
import { useCurrentShipment } from '../../../../../../../hooks/useCurrentShipment';
import { ReturnShipment } from '../../../../../models/ReturnShipment.model';
import { DropOffCard } from '../../../../DropOffCard';
import { useBooking } from '../../../hooks';
import {
  ActionWrapper,
  Button,
  Container,
  DropOffDescriptionContainer,
  ErrorMessage,
  NoteText,
} from './ConfirmDropOffLayout.styles';

interface ConfirmDropOffLayoutProps {
  selectedDropOff: PudoLocation;
  goToPreviousStep: () => void;
  closeDropOffModal: () => void;
  shipment: ReturnShipment;
}

const ConfirmDropOffLayout: FunctionComponent<ConfirmDropOffLayoutProps> = ({
  selectedDropOff,
  goToPreviousStep,
  closeDropOffModal,
  shipment,
}): JSX.Element => {
  const { t } = useTranslation();
  const { isMobile } = useDetectDevice();
  const { onBooking, process } = useBooking({
    collectionPoint: selectedDropOff,
    shipmentPk: shipment.id,
    shipmentUuid: shipment.parcelperformId,
    returnUuid: shipment.returnTicket.returnUuid,
    returnStatus: shipment.returnTicket.returnStatus,
    bookingCount: shipment.returnTicket.bookingCount,
  });
  const { reloadShipment } = useCurrentShipment();

  useEffect(() => {
    if (!process.error) {
      return;
    }

    const ERROR_KEYS = ['book_carrier_failed', 'shipment_booked'];

    const shouldReloadShipment = ERROR_KEYS.includes(
      process.error?.response?.data?.errors?.key,
    );

    if (shouldReloadShipment) {
      reloadShipment();
      closeDropOffModal();
    }
  }, [process.error]);

  useEffect(() => {
    if (process.success) {
      reloadShipment();
      closeDropOffModal();
    }
  }, [process.success]);

  const onBook = async () => {
    await onBooking();
  };

  const { isAllowGoogleMaps } = useLocationConsent();

  const renderBody = () => {
    if (process.loading) {
      return <LoadingScreen />;
    }

    return (
      <>
        <DropOffDescriptionContainer
          isFullWidth
          justify="space-between"
          direction="column"
        >
          <DropOffCard
            dropOff={selectedDropOff}
            expanded
            isViewOnly
            isShowDropoffMap={isAllowGoogleMaps}
          />
          <NoteText>{t('RETURN_NO_CHANGE_DROPOFF')}</NoteText>
          {process.error && (
            <ErrorMessage>{process.error.message}</ErrorMessage>
          )}
        </DropOffDescriptionContainer>
        <ActionWrapper
          isFullWidth
          direction={isMobile ? 'column' : 'row'}
          justify={isMobile ? 'center' : 'flex-end'}
          gap={8}
        >
          <Button
            variant="outlined"
            onClick={() => {
              goToPreviousStep();
            }}
            isMobile={isMobile}
          >
            {t('BACK_CTA')}
          </Button>
          <Button onClick={onBook} isMobile={isMobile}>
            {t('CONFIRM_CTA')}
          </Button>
        </ActionWrapper>
      </>
    );
  };

  return (
    <Container
      isMobile={isMobile}
      align="center"
      justify={process.loading ? 'center' : 'space-between'}
      direction="column"
    >
      {renderBody()}
    </Container>
  );
};

export { ConfirmDropOffLayout };
